import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
} from 'react'
import Page from 'containers/Page'
import Voice from 'components/Voice'
import VoiceGrid from 'components/VoiceGrid'
import { Card, Chip, } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import { ProjectContext } from 'contexts/ProjectContext'
import {
  useMatch,
  useLocation,
  useSearchParams,
  useParams,
} from 'react-router-dom'
import genderNames from 'data/genders/messages'
import languageNames from 'data/languages/messages'
import messages from './messages'

const EmbeddedProjectPage = () => {
  let [searchParams, setSearchParams] = useSearchParams()

  const location = useLocation()
  const match = useMatch(location.pathname)
  const { formatMessage, locale } = useIntl()
  const { hashId } = useParams()
  const { project } = useContext(ProjectContext)
  const [genders, setGenders] = useState({})
  const [languages, setLanguages] = useState({})
  const availableGenders = useMemo(() => [... new Set(project.suppliers?.map(({ gender }) => gender))], [project.suppliers])
  const availableLanguages = useMemo(() => [... new Set(project.suppliers?.flatMap(({ languages }) => languages))], [project.suppliers])

  useEffect(() => {
    if (!project.id && hashId) {
      project.fetch(hashId)
    }
  }, [project.id])

  useEffect(() => {
    if (project.loading) {
      return
    }

    setLanguages(
      project
        .languages
        .reduce((memo, language) => ({
          ...memo,
          [language]: true
        }), {})
    )
  }, [project.languages])

  const voiceGroups = useMemo(() => {
    const selectedLanguages = Object.entries(languages).filter(([, isSet]) => !!isSet).map(([k]) => k)
    const selectedGenders = Object.entries(genders).filter(([, isSet]) => !!isSet).map(([k]) => k)

    let groups = Object.values(project.voices || {})
      .filter(
        (voice) =>
        (!selectedLanguages.length ? true : selectedLanguages.some((iso) => voice.languages?.includes(iso))) &&
          (!selectedGenders.length ? true : selectedGenders.some((gender) => voice.gender == gender))
      )
      .reduce((memo, voice, i) => {
        const key = voice.character || 'default'

        if (!Array.isArray(memo[key])) {
          memo[key] = []
        }

        let props = {
          Component: Voice,
          key: `${key}-${voice.id}-${i}`,
          voice: {
            ...voice,
            demos: (voice.demos || []).filter(({ language }) => (
              !selectedLanguages.length ? true : selectedLanguages.includes(language))
            ),
          },
          dispatch: null,
          product: project.category,
          readOnly: true,
          downloadable: false,
          expandable: true,
          editable: false,
        }

        memo[key].push(props)

        return memo
      }, {})

    return groups || {}
  }, [project, languages, genders])

  const { default: ungroupedVoices = {}, ...groupedVoices } = voiceGroups

  return (
    <Page
      childrenProps={{ className: '' }}
      nav={false}
      footer={false}
    >
      <main className="p-0 m-0">
        <div className="sticky top-0 left-0 w-full py-4 bg-white z-30 space-y-2">
          <div className="w-full flex items-start space-x-0 space-y-2 flex-col md:flex-row md:space-x-6 md:space-y-0">
            { availableGenders.length > 1 &&
              <div className="flex gap-2 items-center">
                <label className="text-gray-400 font-medium text-xs"><FormattedMessage {...messages.genders}/></label>
                { availableGenders.map((gender) => (
                  <Chip
                    key={`gender-${gender}`}
                    onClick={() => setGenders((prev) => ({ ...prev, [gender]: !prev[gender] }))}
                    state={genders[gender] ? 'info' : undefined}
                  >
                    <FormattedMessage {...genderNames[gender]} />
                  </Chip>
                )) }
              </div>
            }

            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-gray-400 font-medium text-xs"><FormattedMessage {...messages.languages}/></label>
              { availableLanguages.map((language) => (
                <Chip
                  key={`language-${language}`}
                  onClick={() => setLanguages((prev) => ({ ...prev, [language]: !prev[language] }))}
                  state={languages[language] ? 'info' : undefined}
                >
                  <span className="whitespace-nowrap">
                    <FormattedMessage {...languageNames[language]} />
                  </span>
                </Chip>
              )) }
            </div>
          </div>
        </div>

        <div
          className={!!Object.keys(groupedVoices).length ? 'p-2 mt-10' : ''}
        >
          <VoiceGrid view="MASONRY">
            {!Object.keys(ungroupedVoices).length && project.loading
              ? [...new Array(9)].map((_, i) => (
                <Card
                  key={`loading-${i}`}
                  className="bg-white break-inside-avoid animate-pulse p-4 space-y-4 border-l-4 border-gray-100"
                >
                  <div className="bg-gray-50 w-1/4 h-4 block" />
                  <div className="bg-gray-50 w-2/3 h-4 block" />
                  <div className="bg-gray-50 w-1/3 h-4 block" />
                  <div className="flex space-x-2">
                    <div className="bg-gray-50 w-10/12 h-10 block" />
                    <div className="bg-gray-100 w-2/12 h-10 block" />
                  </div>
                </Card>
              ))
              : Object.values(ungroupedVoices).map(
                ({ Component, ...props }) => <Component {...props} />
              )}
          </VoiceGrid>
        </div>
      </main>
    </Page>
  )
}

export default EmbeddedProjectPage
